import { OnAnimationFrame } from "../animation/OnAnimationFrame";
import { MathX } from "../math/MathX";
import { Arrays } from "../tools/Arrays";
import { AudioGraph } from "./graph/AudioGraph";
import { AudioGraphNode } from "./graph/nodes/AudioGraphNode";
import { BiquadFilter } from "./graph/nodes/BiquadFilter";
import { DynamicsCompressor } from "./graph/nodes/DynamicsCompressor";
import { Gain } from "./graph/nodes/Gain";
import { MediaElementAudioSource } from "./graph/nodes/MediaElementAudioSource";
import { UserAgentInfo } from "../../library-js/dom/UserAgentInfo";
import { AudioClip } from "./AudioClip";
import { EventSlot } from "../events/EventSlot";

export class AudioPlayer
{
  static _instance:AudioPlayer;

  static get $():AudioPlayer
  {
    if ( AudioPlayer._instance )
    {
      return 
    } 

    AudioPlayer._instance = new AudioPlayer( AudioGraph.$ );
  }

  private _audioGraph:AudioGraph;
  _audioElement:HTMLAudioElement;
  private _gain:Gain;
  
  private _subPush:BiquadFilter;
  private _trebleDuck:BiquadFilter;
  private _brilliancePush:BiquadFilter;

  private _limiter:DynamicsCompressor;

  private _elementSource:MediaElementAudioSource;
  private _alive:boolean = true;
  get activated(){ return this._alive && this._audioGraph.context; }
  

  private _volume:number = 1;
  private _volumeMultiply:number = 0.7;
  private _cachedBass:number = 0;
  private _cachedTreble:number = 0;
  private _hasParameterChanges = false;

  private static _onAnimationFrame:OnAnimationFrame;
  private static _players:AudioPlayer[] = [];
  private static schedule( player:AudioPlayer )
  {
    if ( ! AudioPlayer._onAnimationFrame )
    {
      AudioPlayer._onAnimationFrame = new OnAnimationFrame();
      AudioPlayer._onAnimationFrame.run();
      AudioPlayer._onAnimationFrame.addListener( 
        () => 
        {
          AudioPlayer._players.forEach( p => p._update() );
        }
      );
    }

    AudioPlayer._players.push( player );   
  }

  private static unschedule( player:AudioPlayer )
  {
    Arrays.remove(AudioPlayer._players, player );
  }
  

  constructor( audioGraph:AudioGraph, audio:HTMLAudioElement = null, simpleMode:boolean = false )
  {
    this._audioGraph = audioGraph;  
    this._audioElement = audio;
    this._simpleMode = simpleMode;

    this._createGraph();
    AudioPlayer.schedule( this );
  }

  destroy()
  {
    if ( ! this._alive )
    {
      return;
    }

    this.pause();
    this._audioGraph.close();
    AudioPlayer.unschedule( this );
  }

  private _update()
  {
    if ( ! this._alive )
    {
      return;
    }
    
    if ( this._currentClip && ! this._audioElement.paused )
    {
      this._currentClip._updatePosition( this );
    }

    if ( ! this._audioGraph.ready )
    {
      return;
    }

    if ( ! this._hasParameterChanges )
    {
      return;
    }

    this._gain.node.gain.value = this._volume * this._volumeMultiply;

    if ( this._simpleMode )
    {
      this._hasParameterChanges = false;
      return;
    }

    
    let bass = this._cachedBass;
    let treble = this._cachedTreble;

    let subGain        = bass * 20;
    let brillianceGain = treble * 12;
    let trebleDuck     = ( Math.max( 0, bass ) + Math.max( treble ) ) * - 15;

    this._subPush.setPeaking( 30, 1/2, subGain );
    this._trebleDuck.setPeaking( 2000, 1/4, trebleDuck );
    this._brilliancePush.setPeaking( 10000, 1, brillianceGain );

    let limiterStrength = Math.min( Math.abs( bass ) * 2 + Math.abs( treble ), 1 );
    this._limiter.setLimiter( limiterStrength );

    this._hasParameterChanges = false;
  }

  get volume()
  {
    if ( ! this._alive )
    {
      return 0;
    }

    if ( ! this._audioGraph.ready )
    {
      return this._volume; 
    }

    return this._gain.node.gain.value;
  }

  set volume( value:number )
  {
    this._hasParameterChanges = true;
    this._volume = value;
  }

  get playedSeconds()
  {
    if ( ! this._audioElement )
    {
      return 0;
    } 

    return this._audioElement.currentTime;
  }

  get playState()
  {
    return this.playedSeconds / this.audioDuration;
  }

  get audioDuration()
  {
    if ( ! this._audioElement )
    {
      return 0;
    } 

    return this._audioElement.duration;
  }

  _simpleMode = false;


  private _createGraph()
  {    
    this._audioElement = this._audioElement || document.createElement( "audio" );
    
    this._audioElement.addEventListener( 
      "canplay", 
      ()=>
      {
        this._canPlayReady = true;
        this._onCanPlay.dispatch( null );
      }
    );

    this._audioElement.addEventListener( 
      "canplaythrough", 
      ()=>
      {
        this._canPlayThroughReady = true;
        this._onCanPlayThrough.dispatch( null );
      }
    );

    this._audioElement.addEventListener( 
      "ended", 
      ()=>
      {
        if ( this._currentClip )
        {
          this._currentClip.setEnded( this );
        }

        console.log( "ENDED" );
      }
    );

    let ag = this._audioGraph;

    this._elementSource = ag.createMediaElementSource( this._audioElement );  
    
    this._gain = ag.createGain();

    if ( this._simpleMode || UserAgentInfo.isIPad || UserAgentInfo.isIPhone )
    {
      this._simpleMode = true;

      this._volumeMultiply = 1;

      AudioGraphNode.connectLinearToOutput(
        [
          this._elementSource,
          this._gain
        ]
      );

      return;
    }
    

    this._subPush        = ag.createBiquadFilter();
    this._trebleDuck     = ag.createBiquadFilter();
    this._brilliancePush = ag.createBiquadFilter();

    

    this._limiter = ag.createDynamicsCompressor();
    
    AudioGraphNode.connectLinearToOutput(
      [
        this._elementSource,
        this._subPush,
        this._trebleDuck,
        this._brilliancePush,
        this._gain,
        this._limiter
      ]
    );

    ag.onReady.addListener(
      ()=>
      {
        this.setHiFiSettings( 0, 0 );

        this._limiter.setLimiter( 0 );
      } 
    )
  } 

  get bass()
  {
    return this._cachedBass;
  }

  get treble()
  {
    return this._cachedTreble;
  }

  setBass( bass:number )
  {
    this.setHiFiSettings( bass, this._cachedTreble );
  }

  setTreble( treble:number )
  {
    this.setHiFiSettings( this._cachedBass, treble );
  }
  
  setHiFiSettings( bass:number, treble:number )
  {
    this._cachedBass   = MathX.clamp01( bass );
    this._cachedTreble = MathX.clamp01( treble );

    this._hasParameterChanges = true;
  }

  setPosition( position:number )
  {
    this._audioElement.currentTime = position;
  }

  private _currentClip:AudioClip;
  isCurrentClip( clip:AudioClip )
  {
    return this._currentClip === clip;
  }

  setAudioClip( clip:AudioClip )
  {
    this._currentClip = clip;
  }

  private _canPlayReady = false;
  get canPlay(){ return this._canPlayReady; }
  private _canPlayThroughReady = false;
  get canPlayThrough(){ return this._canPlayThroughReady; }
  
  private _onCanPlay:EventSlot<string> = new EventSlot();
  private _onCanPlayThrough:EventSlot<string> = new EventSlot();

  setSource( src:string )
  {    
    if ( ! this._alive )
    {
      return;
    }

    this._canPlayReady = false;
    this._canPlayThroughReady = false;
    this._audioGraph.initialize();
    this._audioElement.src = src; 
  }

  setSourceAndPlayAt( src:string, position:number, callback:()=>void )
  {
    if ( ! this._alive )
    {
      return;
    }

    if ( ! this._canPlayReady )
    {
      return;
    }

    this._onCanPlayThrough.once(
      ()=>
      {
        this.setPosition( position );
        this.play();
        this.setPosition( position );
        callback();
      }
    )
    
    
    this.setSource( src );
    this.setPosition( position );

  }


  play()
  {
    if ( ! this._alive )
    {
      return;
    }

    this._audioGraph.initialize();
    this._audioElement.play(); 
  }

  pause()
  {
    if ( ! this._alive )
    {
      return;
    }
    this._audioGraph.initialize();
    this._audioElement.pause();
  }

 

  


}